import { CHART_COLORS, paletteAt } from "./palette";

export interface BarListDatum {
  label: string;
  value: number;
}

interface BarListProps {
  data: BarListDatum[];
  /** Format do valor exibido a direita. */
  valueFormatter?: (value: number) => string;
  /** Quando true (default), cada item recebe cor da paleta (cycling).
   *  False -> todos usam ``barColor``. */
  multicolor?: boolean;
  barColor?: string;
  /** Esconde o percentual ao lado do label. Default false. */
  hidePercent?: boolean;
}

/**
 * Lista compacta no estilo "leaderboard":
 * ``label   29%                     valor``
 * ``──────────────                       `` (barra fina proporcional ao max)
 *
 * Substitui ``BarChartHorizontal`` em contextos onde queremos algo
 * mais limpo e textual (sem axis/grid). Renderiza naturalmente sem
 * altura fixa -- ideal pra top-N curto (5-15 items).
 */
export function BarList({
  data,
  valueFormatter = (v) => String(v),
  multicolor = true,
  barColor = CHART_COLORS.brand,
  hidePercent = false,
}: BarListProps) {
  const total = data.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
  const max = data.reduce(
    (acc, d) => Math.max(acc, Number(d.value) || 0),
    0,
  );
  if (data.length === 0) return null;
  return (
    <ul className="flex flex-col gap-3">
      {data.map((d, idx) => {
        const v = Number(d.value) || 0;
        const pct = total > 0 ? (v / total) * 100 : 0;
        const widthPct = max > 0 ? (v / max) * 100 : 0;
        const color = multicolor ? paletteAt(idx) : barColor;
        return (
          <li
            key={`${d.label}-${idx}`}
            className="flex min-w-0 flex-col gap-1.5"
          >
            <div className="flex min-w-0 items-baseline justify-between gap-2 text-sm">
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span
                  className="truncate font-medium text-ink-900"
                  title={d.label}
                >
                  {d.label}
                </span>
                {!hidePercent ? (
                  <span className="tabular shrink-0 text-[11px] text-ink-500">
                    {pct.toFixed(pct >= 10 ? 0 : 1)}%
                  </span>
                ) : null}
              </span>
              <span className="tabular shrink-0 text-xs font-medium text-ink-900">
                {valueFormatter(v)}
              </span>
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full"
              style={{ backgroundColor: `${CHART_COLORS.line}` }}
            >
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{
                  width: `${Math.max(2, widthPct)}%`,
                  backgroundColor: color,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
